import { filterByUniqueKey } from '@kk/shared/utils/iterables';
import { isNullish } from '@kk/shared/utils/type-guards';
import type { Column, Row } from '@tanstack/table-core';
import clsx from 'clsx';
import { type FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../../Button/Button';
import Icon from '../../Icon';
import MarkMatches from '../../MarkMatches/MarkMatches';

interface CheckboxFilterProps<T> {
  data: T[];
  filteredRows: Row<T>[];
  column: Column<T, unknown> | undefined;
  close: () => void;
  label: React.ReactNode;
}

export function CheckboxFilter<T>(props: CheckboxFilterProps<T>) {
  const { t } = useTranslation('ui');
  const showSearch = props.column?.columnDef.meta?.showSearchInFilter ?? true;
  const [search, setSearch] = useState<string>('');
  const values: string[] = Array.isArray(props.column?.getFilterValue())
    ? (props.column.getFilterValue() as string[])
    : [];

  const options = useMemo(() => {
    const uniqueValues: { value: string; original: T }[] = filterByUniqueKey(
      props.data.map((row, index) => {
        const original = props.column?.accessorFn?.(row, index);
        // if a custom filterOptionValueAccessor is provided, use it to get determine the value
        if (
          typeof props.column?.columnDef.meta?.filterOptionValueAccessor ===
          'function'
        ) {
          return {
            original,
            value:
              props.column.columnDef.meta.filterOptionValueAccessor(original),
          };
        }
        return { value: original, original };
      }),
      'value',
      // exclude empty values as options
    ).filter(({ original }) => !isNullish(original)) as {
      value: string;
      original: T;
    }[];

    if (search === '' || showSearch === false)
      return typeof props.column?.columnDef.meta?.filterOptionSortFn ===
        'function'
        ? uniqueValues.sort(
            (a, b) =>
              props.column?.columnDef.meta?.filterOptionSortFn?.(
                a.original,
                b.original,
              ) ?? 0,
          )
        : uniqueValues;

    // if search is enabled and not empty, filter the unique values based on the input value
    const searchMatches = uniqueValues.filter(
      ({ value }) =>
        value.toLowerCase().includes(search.toLowerCase()) ||
        props.column?.columnDef.meta
          ?.filterOptionLabel?.(value, props.column)
          ?.toLowerCase()
          .includes(search.toLowerCase()),
    );

    return typeof props.column?.columnDef.meta?.filterOptionSortFn ===
      'function'
      ? searchMatches.sort(
          (a, b) =>
            props.column?.columnDef.meta?.filterOptionSortFn?.(
              a.original,
              b.original,
            ) ?? 0,
        )
      : searchMatches;
  }, [props.data, props.column, search, showSearch]);

  const [internalFilterValues, setInternalFilterValues] =
    useState<string[]>(values);

  const resultCount = useMemo(() => {
    return internalFilterValues.length === 0
      ? props.filteredRows.length
      : props.filteredRows.filter((row, index) =>
          internalFilterValues.includes(
            typeof props.column?.columnDef.meta?.filterOptionValueAccessor ===
              'function'
              ? (props.column.columnDef.meta.filterOptionValueAccessor(
                  props.column.accessorFn?.(row.original, index),
                ) as string)
              : (props.column?.accessorFn?.(row.original, index) as string),
          ),
        ).length;
  }, [internalFilterValues, props.filteredRows, props.column]);

  const getFilterFormValues = (e: FormEvent<HTMLFormElement>) => {
    if (!props.column?.id)
      throw new TypeError('Cannot filter without column id');
    const formData = new FormData(e.currentTarget);
    const filters = Array.from(formData.getAll(props.column.id)).map((v) =>
      v.toString(),
    );

    return filters;
  };

  const optionsRef = useRef<HTMLOListElement | null>(null);

  useEffect(() => {
    const firstCheckedOption =
      optionsRef.current?.querySelector('input:checked');
    if (firstCheckedOption) {
      firstCheckedOption.scrollIntoView({
        behavior: 'instant',
        block: 'center',
      });
    }
  }, []);

  return (
    <div className="py-1">
      {showSearch ? (
        <>
          <div className="laane:hidden md:hidden px-2 pt-11 pb-7 flex justify-between items-center">
            <h2 className="typ-headline-lg">
              {props.column?.columnDef.meta?.filterTitleOverride ?? (
                <>
                  <span className="mr-2">{t('select')}</span>
                  <span className="lowercase">{props.label}</span>
                </>
              )}
            </h2>
            <button onClick={props.close}>
              <Icon type="cross" size="small" />
            </button>
          </div>
          <div className="relative">
            <Icon
              type="magnify"
              className="pointer-events-none absolute top-1/2 -translate-y-1/2 translate-x-2"
            />
            <input
              onChange={(e) => {
                setSearch(e.target.value);
              }}
              name={t('search')}
              value={search}
              type="search"
              className="w-full p-2 pl-10 kunde:pl-10"
              placeholder={
                props.column?.columnDef.meta?.filterPlaceholder ?? t('search')
              }
            />
          </div>
        </>
      ) : null}
      <form
        name={`filter-${props.column?.id}`}
        className="space-y-1"
        onSubmit={(e: FormEvent<HTMLFormElement>) => {
          e.preventDefault();
          props.column?.setFilterValue(getFilterFormValues(e));

          if (typeof props.close === 'function') {
            props.close();
          }
        }}
        onChange={(e: FormEvent<HTMLFormElement>) => {
          setInternalFilterValues(getFilterFormValues(e));
        }}
        onReset={() => {
          props.column?.setFilterValue([]);
          setInternalFilterValues([]);
          if (typeof props.close === 'function') {
            props.close();
          }
        }}
      >
        {options.length <= 0 ? (
          <div className="typ-body-xs px-2 py-4 text-center text-neutral-100">
            {t('noSearchResults')}
          </div>
        ) : (
          <ol
            className="max-h-80 space-y-2 overflow-y-auto px-1 py-2"
            ref={optionsRef}
          >
            {options
              .map((option) => {
                if (!option.value) return null;
                const labelText =
                  props.column?.columnDef.meta?.filterOptionLabel?.(
                    option.original,
                    props.column,
                  ) ?? option.value;

                const label =
                  typeof props.column?.columnDef.meta?.renderFilterOption ===
                  'function' ? (
                    props.column.columnDef.meta.renderFilterOption(
                      option.original,
                      props.column,
                    )
                  ) : (
                    <MarkMatches text={labelText} search={search} />
                  );

                if (label === null) return null;
                return (
                  <li key={props.column?.id + option.value}>
                    <label
                      className={clsx(
                        'ring-blue-20 focus-within:bg-neutral-5 focus-within:text-blue-60 hover:bg-neutral-5 focus-within:font-semibold',
                        'flex cursor-pointer items-center space-x-4 rounded px-4 py-2 focus-within:ring-2',
                      )}
                    >
                      <input
                        type="checkbox"
                        name={props.column?.id}
                        value={option.value}
                        defaultChecked={values.includes(option.value)}
                      />
                      <span className="typ-body-xs line-clamp-1 block truncate">
                        {label}
                      </span>
                    </label>
                  </li>
                );
              })
              .filter(Boolean)}
          </ol>
        )}
        <div className="space-y-2">
          <Button
            type="submit"
            theme="primary"
            className="flex w-full justify-center p-4"
            data-testid="filter-submit"
          >
            <span>{t('table.filter.showResults', { count: resultCount })}</span>
          </Button>
          <Button
            theme="tertiary"
            type="reset"
            disabled={internalFilterValues.length === 0}
            data-testid="filter-clear"
            className="flex w-full justify-center p-4"
          >
            <span> {t('table.filter.clear')}</span>
          </Button>
        </div>
      </form>
    </div>
  );
}
